/**
 * Generates and prints a Verification Certificate PDF for a C2PA-verified file.
 * Uses the browser's native print dialog (no extra dependencies).
 * The user can save as PDF from the print dialog.
 *
 * Adapted from verifieddit-www for the Phenom Drop upload portal.
 */

interface CertFileInfo {
  name: string
  type: string
  size: number
  hasC2PA: boolean
  fileHash?: string
  shortUrl?: string
  thumbnail?: string
  aiDetected?: { isAiGenerated: boolean; note: string }
  c2paDetails?: {
    hasValidSignature: boolean
    hasTrustedSigner: boolean
    hasErrors: boolean
    hasWarnings: boolean
    activeManifest?: {
      title?: string
      claimGenerator?: string | null
      signatureInfo?: {
        issuer?: string
        time?: string
      } | null
      ingredients?: Array<{ title: string; format: string }>
    }
  }
}

function statusLabel(file: CertFileInfo): { text: string; color: string; bg: string; border: string } {
  if (!file.hasC2PA || !file.c2paDetails) {
    return { text: 'No File Details', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' }
  }
  const d = file.c2paDetails
  if (d.hasErrors) return { text: 'Invalid — Integrity Failure', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' }
  if (d.hasValidSignature && d.hasTrustedSigner) return { text: 'Trusted — Verified Signer', color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' }
  if (d.hasValidSignature) return { text: 'Valid — Signer Not Trusted', color: '#d97706', bg: '#fffbeb', border: '#fde68a' }
  return { text: 'Unsigned', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function formatDate(isoString?: string): string {
  if (!isoString) return 'Unknown'
  try {
    return new Date(isoString).toLocaleString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    })
  } catch { return isoString }
}

export function exportVerificationCertificate(file: CertFileInfo): void {
  const status = statusLabel(file)
  const verifiedAt = new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  })
  const am = file.c2paDetails?.activeManifest
  const sigTime = formatDate(am?.signatureInfo?.time)
  const ingredients = am?.ingredients ?? []
  const ai = file.aiDetected

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Verification Certificate — ${file.name}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', system-ui, sans-serif; background: #fff; color: #1e293b; font-size: 14px; line-height: 1.5; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none; }
  }
  .cert { max-width: 760px; margin: 0 auto; padding: 40px 48px; }
  .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 24px; border-bottom: 2px solid #1e293b; margin-bottom: 32px; }
  .brand { display: flex; align-items: center; gap: 12px; }
  .logo-box { width: 40px; height: 40px; background: #1e293b; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
  .brand-name { font-size: 20px; font-weight: 700; color: #1e293b; }
  .cert-title { font-size: 13px; font-weight: 500; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; }
  .status-badge { display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 8px; border: 1.5px solid ${status.border}; background: ${status.bg}; margin-bottom: 28px; }
  .status-dot { width: 10px; height: 10px; border-radius: 50%; background: ${status.color}; flex-shrink: 0; }
  .status-text { font-size: 15px; font-weight: 600; color: ${status.color}; }
  .section { margin-bottom: 24px; }
  .section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; margin-bottom: 10px; }
  .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .field { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; }
  .field:nth-last-child(-n+2) { border-bottom: none; }
  .field-label { font-size: 11px; font-weight: 500; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 2px; }
  .field-value { font-size: 13px; color: #1e293b; font-weight: 500; word-break: break-all; }
  .full-width { grid-column: 1 / -1; }
  .ingredient-list { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .ingredient-item { display: flex; align-items: center; gap: 8px; padding: 8px 14px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
  .ingredient-item:last-child { border-bottom: none; }
  .ingredient-dot { width: 7px; height: 7px; border-radius: 50%; background: #10b981; flex-shrink: 0; }
  .hash-box { padding: 10px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; font-family: 'Menlo', 'Monaco', monospace; font-size: 11px; word-break: break-all; color: #475569; }
  .hash-label { font-size: 11px; font-weight: 500; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  .short-url-box { display: flex; align-items: center; gap: 12px; padding: 14px; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 8px; margin-bottom: 24px; }
  .short-url-label { font-size: 11px; color: #6366f1; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
  .short-url-link { font-size: 14px; color: #4338ca; font-weight: 600; text-decoration: none; }
  .ai-alert { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #fdf2f8; border: 1.5px solid #f9a8d4; border-radius: 8px; margin-bottom: 24px; }
  .ai-dot { width: 10px; height: 10px; border-radius: 50%; background: #ec4899; flex-shrink: 0; }
  .ai-text { font-size: 13px; color: #9d174d; font-weight: 600; }
  .ai-note { font-size: 12px; color: #be185d; font-weight: 400; }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
  .footer-left { font-size: 12px; color: #94a3b8; }
  .footer-url { font-size: 12px; font-weight: 600; color: #3b82f6; }
  .print-btn { position: fixed; bottom: 24px; right: 24px; background: #1e293b; color: #fff; border: none; padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .print-btn:hover { background: #334155; }
</style>
</head>
<body>
<div class="cert">
  <div class="header">
    <div class="brand">
      <div class="logo-box">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div>
        <div class="brand-name">Verifieddit</div>
        <div style="font-size:11px;color:#64748b;">verifieddit.com</div>
      </div>
    </div>
    <div class="cert-title">Verification Certificate</div>
  </div>

  <div class="status-badge">
    <div class="status-dot"></div>
    <div class="status-text">${status.text}</div>
  </div>

  ${ai?.isAiGenerated ? `
  <div class="ai-alert">
    <div class="ai-dot"></div>
    <div>
      <div class="ai-text">AI-Generated Content Detected</div>
      <div class="ai-note">${ai.note}</div>
    </div>
  </div>` : ''}

  ${file.thumbnail ? `
  <div style="text-align:center;margin-bottom:24px;">
    <img src="${file.thumbnail}" style="max-width:300px;max-height:200px;border-radius:8px;border:1px solid #e2e8f0;" alt="${file.name}" />
  </div>` : ''}

  <div class="section">
    <div class="section-title">File Information</div>
    <div class="field-grid">
      <div class="field full-width">
        <div class="field-label">Filename</div>
        <div class="field-value">${file.name}</div>
      </div>
      <div class="field">
        <div class="field-label">File Type</div>
        <div class="field-value">${file.type || 'Unknown'}</div>
      </div>
      <div class="field">
        <div class="field-label">File Size</div>
        <div class="field-value">${formatBytes(file.size)}</div>
      </div>
      <div class="field">
        <div class="field-label">Verified At</div>
        <div class="field-value">${verifiedAt}</div>
      </div>
      <div class="field">
        <div class="field-label">File Details</div>
        <div class="field-value">${file.hasC2PA ? 'Present' : 'Not found'}</div>
      </div>
    </div>
  </div>

  ${file.hasC2PA && am ? `
  <div class="section">
    <div class="section-title">File Provenance</div>
    <div class="field-grid">
      ${am.signatureInfo?.issuer ? `
      <div class="field full-width">
        <div class="field-label">Signer (Certificate Issuer)</div>
        <div class="field-value">${am.signatureInfo.issuer}</div>
      </div>` : ''}
      ${am.claimGenerator ? `
      <div class="field full-width">
        <div class="field-label">Claim Generator</div>
        <div class="field-value">${am.claimGenerator}</div>
      </div>` : ''}
      <div class="field">
        <div class="field-label">Signed At</div>
        <div class="field-value">${sigTime}</div>
      </div>
      <div class="field">
        <div class="field-label">Ingredient Count</div>
        <div class="field-value">${ingredients.length} ${ingredients.length === 1 ? 'source' : 'sources'}</div>
      </div>
    </div>
  </div>

  ${ingredients.length > 0 ? `
  <div class="section">
    <div class="section-title">Source Ingredients</div>
    <div class="ingredient-list">
      ${ingredients.map(ing => `
      <div class="ingredient-item">
        <div class="ingredient-dot"></div>
        <span>${ing.title || 'Untitled'}</span>
        ${ing.format ? `<span style="color:#94a3b8;font-size:12px;">(${ing.format.split('/')[1]?.toUpperCase() ?? ing.format})</span>` : ''}
      </div>`).join('')}
    </div>
  </div>` : ''}
  ` : `
  <div class="section">
    <div style="padding:20px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;color:#64748b;font-size:13px;">
      No file details found. The file's origin and editing history cannot be verified.
    </div>
  </div>
  `}

  ${file.fileHash ? `
  <div class="section">
    <div class="section-title">File Integrity</div>
    <div class="hash-box">
      <div class="hash-label">SHA-256 Hash</div>
      ${file.fileHash}
    </div>
  </div>` : ''}

  ${file.shortUrl ? `
  <div class="short-url-box">
    <div>
      <div class="short-url-label">Shareable Proof Link</div>
      <a class="short-url-link" href="${file.shortUrl}" target="_blank">${file.shortUrl}</a>
    </div>
  </div>` : ''}

  <div class="footer">
    <div class="footer-left">
      Submitted via Phenom Drop &mdash; Verified by Verifieddit. All analysis performed locally in your browser.
    </div>
    <div class="footer-url">verifieddit.com</div>
  </div>
</div>

<button class="print-btn no-print" onclick="window.print()">Save as PDF</button>

<script>
  window.onload = function() {
    setTimeout(function() { window.print() }, 400)
  }
<\/script>
</body>
</html>`

  const win = window.open('', '_blank', 'width=900,height=700')
  if (win) {
    win.document.write(html)
    win.document.close()
  }
}
